import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

export const AsyncProductAndSubdomainSelect = ({
  onChange,
  value,
  required,
}: FieldExtensionComponentProps<{ product: string; subdomain: string }>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [products, setProducts] = useState<string[]>([]);
  const [subdomains, setSubdomains] = useState<string[]>(['Select Product First']);
  const [selectedProduct, setSelectedProduct] = useState<string>(value?.product || '');
  const [selectedSubdomain, setSelectedSubdomain] = useState<string>(value?.subdomain || '');
  const [error, setError] = useState<string | null>(null);
  const [subdomainError, setSubdomainError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('edm-connector');
      const response = await fetchApi.fetch(`${baseUrl}/product`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch products: ${response.statusText}${errorText && errorText.trim() ? ` (${errorText.trim().slice(0, 200)})` : ''}`
        );
      }

      const productList = await response.json();

      setProducts(Array.isArray(productList) ? productList : []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch products.');
    }
  }, [discoveryApi, fetchApi]);

  const fetchSubdomains = useCallback(async (productName: string) => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('edm-connector');
      const response = await fetchApi.fetch(
        `${baseUrl}/subdomains-for-product?productName=${encodeURIComponent(productName)}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch subdomains for product ${productName}: ${response.statusText}${errorText && errorText.trim() ? ` (${errorText.trim().slice(0, 200)})` : ''}`
        );
      }

      const subdomainList = await response.json();

      if (!Array.isArray(subdomainList) || subdomainList.length === 0) {
        setSubdomains(['Select Product First']);
        setSubdomainError(`No subdomains exist for product ${productName}`);
      } else {
        setSubdomains(subdomainList);
        setSubdomainError(null);
      }
    } catch (err) {
      setSubdomains(['Select Product First']);
      setSubdomainError(`Failed to fetch subdomains for product ${productName}`);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (selectedProduct) {
      fetchSubdomains(selectedProduct);
    } else {
      setSubdomains(['Select Product First']);
      setSubdomainError(null);
    }
  }, [selectedProduct, fetchSubdomains]);

  const handleProductChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newProduct = event.target.value as string;
    setSelectedProduct(newProduct);
    setSelectedSubdomain('');
    onChange?.({ product: newProduct, subdomain: '' });
  };

  const handleSubdomainChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newSubdomain = event.target.value as string;
    setSelectedSubdomain(newSubdomain);
    onChange?.({ product: selectedProduct, subdomain: newSubdomain });
  };

  return (
    <>
      <FormControl margin="normal" required={required} error={!!error} fullWidth>
        <InputLabel id="product-select-label">Product</InputLabel>
        <Select labelId="product-select-label" value={selectedProduct} onChange={handleProductChange}>
          {products.map((product) => (
            <MenuItem key={product} value={product}>
              {product}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText error>{error}</FormHelperText>}
        {!error && <FormHelperText>Select a product from the list.</FormHelperText>}
      </FormControl>

      <FormControl margin="normal" required={required} error={!!subdomainError} fullWidth>
        <InputLabel id="subdomain-select-label">Subdomain</InputLabel>
        <Select
          labelId="subdomain-select-label"
          value={selectedSubdomain}
          onChange={handleSubdomainChange}
          disabled={subdomains.length === 1 && subdomains[0] === 'Select Product First'}
        >
          {subdomains.map((subdomain) => (
            <MenuItem key={subdomain} value={subdomain}>
              {subdomain}
            </MenuItem>
          ))}
        </Select>
        {subdomainError && <FormHelperText error>{subdomainError}</FormHelperText>}
        {!subdomainError && <FormHelperText>Select a subdomain based on the product.</FormHelperText>}
      </FormControl>
    </>
  );
};
